/**
 * GovernanceAgentsPage — Agentes AI (Foundry-driven)
 * Source of truth: Azure AI Foundry
 * VUKZ minimalist design
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Zap, ZapOff, Settings, Trash2,
  CheckCircle, XCircle, AlertTriangle, RotateCcw, Wifi, WifiOff
} from 'lucide-react';
import {
  getGovernanceAgents,
  enableAgent,
  disableAgent,
  updateAgentConfig,
  deleteAgentFromFoundry,
  syncAgentsFromFoundry,
} from '../../services/governance';
import { COLORS } from '../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const AGENTS_LABELS = {
  pt: {
    title: 'Agentes AI',
    agents: (n, enabled) => `${n} agente${n !== 1 ? 's' : ''} · ${enabled} ativo${enabled !== 1 ? 's' : ''}`,
    fallback: 'Foundry indisponível — exibindo dados locais (pode estar desatualizado)',
    allClasses: 'Todas',
    syncBtn: 'Sync Foundry',
    filterClassLabel: 'Classe',
    toggleDisable: 'Desabilitar',
    toggleEnable: 'Habilitar',
    toggleSubtitle: (name) => name,
    reasonPlaceholder: 'Motivo (obrigatório)',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    configTitle: 'Configurar agente',
    modelLabel: 'Modelo',
    maxTokensLabel: 'Max Tokens',
    timeoutLabel: 'Timeout (ms)',
    save: 'Salvar',
    thAgent: 'Agente',
    thClass: 'Classe',
    thModel: 'Modelo',
    thStatus: 'Status',
    thCost: 'Custo 24h',
    thP95: 'p95',
    thErrors: 'Erros',
    thActions: 'Ações',
    empty: 'Nenhum agente encontrado',
    emptySub: 'Verifique a conexão com o Foundry ou remova o filtro.',
    loading: 'Carregando agentes do Foundry…',
    deleteConfirm: (name) => `Deletar "${name}" do Foundry? Ação irreversível.`,
    foundryErrMsg: 'Foundry indisponível — verifique FOUNDRY_ENDPOINT e credenciais no App Service.',
    foundryErrRetry: 'Foundry indisponível. Tente novamente em alguns instantes.',
    errSave: 'Erro ao salvar: ',
    errDelete: 'Erro ao deletar: ',
    titleHintDisable: 'Desabilitar',
    titleHintEnable: 'Habilitar',
    titleHintConfig: 'Configurar',
    titleHintDelete: 'Deletar do Foundry',
  },
  en: {
    title: 'AI Agents',
    agents: (n, enabled) => `${n} agent${n !== 1 ? 's' : ''} · ${enabled} active`,
    fallback: 'Foundry unavailable — showing local data (may be outdated)',
    allClasses: 'All',
    syncBtn: 'Sync Foundry',
    filterClassLabel: 'Class',
    toggleDisable: 'Disable',
    toggleEnable: 'Enable',
    toggleSubtitle: (name) => name,
    reasonPlaceholder: 'Reason (required)',
    cancel: 'Cancel',
    confirm: 'Confirm',
    configTitle: 'Configure agent',
    modelLabel: 'Model',
    maxTokensLabel: 'Max Tokens',
    timeoutLabel: 'Timeout (ms)',
    save: 'Save',
    thAgent: 'Agent',
    thClass: 'Class',
    thModel: 'Model',
    thStatus: 'Status',
    thCost: 'Cost 24h',
    thP95: 'p95',
    thErrors: 'Errors',
    thActions: 'Actions',
    empty: 'No agents found',
    emptySub: 'Check Foundry connection or remove filter.',
    loading: 'Loading agents from Foundry…',
    deleteConfirm: (name) => `Delete "${name}" from Foundry? This is irreversible.`,
    foundryErrMsg: 'Foundry unavailable — check FOUNDRY_ENDPOINT and credentials in App Service.',
    foundryErrRetry: 'Foundry unavailable. Please try again in a few moments.',
    errSave: 'Error saving: ',
    errDelete: 'Error deleting: ',
    titleHintDisable: 'Disable',
    titleHintEnable: 'Enable',
    titleHintConfig: 'Configure',
    titleHintDelete: 'Delete from Foundry',
  },
};

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null;

const ClassBadge = ({ cls }) => {
  const map = {
    A: 'bg-gray-900 text-white',
    B: 'bg-amber-100 text-amber-800',
    C: 'bg-blue-100 text-blue-800',
    D: 'bg-gray-100 text-gray-600',
    FOUNDRY: 'bg-purple-100 text-purple-800',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${map[cls] || map.D}`}>
      {cls}
    </span>
  );
};

const FallbackBanner = ({ t }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-sm">
    <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
    <span className="text-amber-800 font-medium">{t.fallback}</span>
  </div>
);

const ToggleModal = ({ agent, action, onConfirm, onCancel, t }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
        <h3 className="font-extrabold text-gray-900 mb-1">
          {action === 'disable' ? t.toggleDisable : t.toggleEnable} agente
        </h3>
        <p className="text-sm text-gray-500 mb-4">{agent.display_name || agent.agent_name}</p>
        {action === 'disable' && (
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t.reasonPlaceholder}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black mb-4"
            rows={3}
          />
        )}
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50">
            {t.cancel}
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={action === 'disable' && !reason.trim()}
            className="flex-1 px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: action === 'disable' ? '#dc2626' : COLORS.primary }}>
            {t.confirm}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfigModal = ({ agent, onSave, onCancel, t }) => {
  const [model, setModel] = useState(agent.model || 'gpt-4.1-mini');
  const [maxTokens, setMaxTokens] = useState(agent.max_tokens || 4000);
  const [timeoutMs, setTimeoutMs] = useState(agent.timeout_ms || 30000);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
        <h3 className="font-extrabold text-gray-900 mb-1">{t.configTitle}</h3>
        <p className="text-sm text-gray-500 mb-5">{agent.display_name || agent.agent_name}</p>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t.modelLabel}</label>
            <select value={model} onChange={e => setModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none">
              <option value="gpt-4.1">gpt-4.1</option>
              <option value="gpt-4.1-mini">gpt-4.1-mini</option>
              <option value="gpt-4o">gpt-4o</option>
              <option value="gpt-4o-mini">gpt-4o-mini</option>
              <option value="local">local</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t.maxTokensLabel}</label>
            <input type="number" value={maxTokens} onChange={e => setMaxTokens(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              min={0} max={128000} step={1000} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t.timeoutLabel}</label>
            <input type="number" value={timeoutMs} onChange={e => setTimeoutMs(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              min={1000} max={120000} step={1000} />
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <button onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50">
            {t.cancel}
          </button>
          <button onClick={() => onSave({ model, max_tokens: maxTokens, timeout_ms: timeoutMs })}
            className="flex-1 px-4 py-2 rounded-xl text-sm font-bold text-white"
            style={{ backgroundColor: COLORS.primary }}>
            {t.save}
          </button>
        </div>
      </div>
    </div>
  );
};

const GovernanceAgentsPage = () => {
  const [agents, setAgents] = useState([]);
  const [meta, setMeta] = useState({ foundry_connected: null, last_sync: null, source: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [classFilter, setClassFilter] = useState('all');
  const [toggleModal, setToggleModal] = useState(null);
  const [configModal, setConfigModal] = useState(null);

  const { lang } = useGovernanceLang();
  const t = AGENTS_LABELS[lang] || AGENTS_LABELS.pt;

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getGovernanceAgents(classFilter === 'all' ? null : classFilter);
      setAgents(res.data?.agents || res.agents || []);
      setMeta({
        foundry_connected: res.data?.foundry_connected ?? res.foundry_connected ?? null,
        last_sync: res.data?.last_sync ?? res.last_sync ?? null,
        source: res.data?.source ?? res.source ?? null,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [classFilter]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await syncAgentsFromFoundry();
      const result = res.data || res;
      if (result.warning === 'FOUNDRY_UNAVAILABLE' || result.foundry_connected === false) {
        setError(t.foundryErrMsg);
      }
      await fetchAgents();
    } catch (err) {
      // 503 from old backend or network error
      setError(t.foundryErrRetry);
    } finally {
      setSyncing(false);
    }
  };

  const confirmToggle = async (reason) => {
    const { agent, action } = toggleModal;
    try {
      if (action === 'disable') await disableAgent(agent.agent_name, reason);
      else await enableAgent(agent.agent_name);
      setToggleModal(null);
      fetchAgents();
    } catch (err) {
      setError('Erro: ' + err.message);
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(t.deleteConfirm(agent.display_name || agent.agent_name))) return;
    try {
      await deleteAgentFromFoundry(agent.agent_name);
      fetchAgents();
    } catch (err) {
      setError(t.errDelete + err.message);
    }
  };

  const enabledCount = agents.filter(a => a.enabled).length;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
          <h1 className="text-2xl font-extrabold text-gray-900">{t.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-gray-500">
              {loading ? '…' : t.agents(agents.length, enabledCount)}
            </p>
            {meta.foundry_connected === true && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                <Wifi size={11} /> Foundry
              </span>
            )}
            {meta.foundry_connected === false && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                <WifiOff size={11} /> Offline
              </span>
            )}
            {meta.last_sync && (
              <span className="text-xs text-gray-400">sync {fmtTime(meta.last_sync)}</span>
            )}
          </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className="text-sm px-3 py-2 border border-gray-200 rounded-xl bg-white">
            <option value="all">{t.allClasses}</option>
            <option value="A">A — Critical</option>
            <option value="B">B — Core</option>
            <option value="C">C — Support</option>
            <option value="D">D — Backfill</option>
          </select>
          <button
            onClick={handleSync}
            disabled={syncing || loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50">
            <RotateCcw size={14} className={syncing ? 'animate-spin' : ''} />
            {t.syncBtn}
          </button>
          <button
            onClick={fetchAgents}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {meta.source === 'constants_fallback' && <FallbackBanner t={t} />}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm">
          <XCircle size={15} className="text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <RefreshCw size={28} className="animate-spin text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{t.loading}</p>
        </div>
      ) : agents.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <p className="font-bold text-gray-700 mb-1">{t.empty}</p>
          <p className="text-sm text-gray-400">{t.emptySub}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase">{t.thAgent}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase">{t.thClass}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase">{t.thModel}</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-400 uppercase">{t.thStatus}</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-400 uppercase">{t.thCost}</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-400 uppercase">{t.thP95}</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-400 uppercase">{t.thErrors}</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-400 uppercase">{t.thActions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {agents.map((agent) => (
                <tr key={agent.agent_name} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{agent.display_name || agent.agent_name}</p>
                    {agent.description && (
                      <p className="text-xs text-gray-400 truncate max-w-xs">{agent.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3"><ClassBadge cls={agent.classification} /></td>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-gray-600">{agent.model || '—'}</span></td>
                  <td className="px-4 py-3 text-center">
                    {agent.enabled
                      ? <CheckCircle size={17} className="inline text-green-600" />
                      : <XCircle size={17} className="inline text-gray-300" />}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-gray-700">${(agent.cost_24h || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-gray-500">{agent.p95_latency_ms || 0}ms</td>
                  <td className={`px-4 py-3 text-right font-mono text-xs ${(agent.error_rate_pct || 0) > 5 ? 'text-rose-600 font-bold' : 'text-gray-500'}`}>
                    {(agent.error_rate_pct || 0).toFixed(1)}%
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {agent.enabled ? (
                        <button onClick={() => setToggleModal({ agent, action: 'disable' })} title={t.titleHintDisable}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors">
                          <ZapOff size={14} />
                        </button>
                      ) : (
                        <button onClick={() => setToggleModal({ agent, action: 'enable' })} title={t.titleHintEnable}
                          className="p-1.5 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors">
                          <Zap size={14} />
                        </button>
                      )}
                      <button onClick={() => setConfigModal(agent)} title={t.titleHintConfig}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                        <Settings size={14} />
                      </button>
                      <button onClick={() => handleDelete(agent)} title={t.titleHintDelete}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toggleModal && (
        <ToggleModal
          agent={toggleModal.agent}
          action={toggleModal.action}
          onConfirm={confirmToggle}
          onCancel={() => setToggleModal(null)}
          t={t}
        />
      )}
      {configModal && (
        <ConfigModal
          agent={configModal}
          onSave={async (config) => {
            try {
              await updateAgentConfig(configModal.agent_name, config);
              setConfigModal(null);
              fetchAgents();
            } catch (err) {
              setError(t.errSave + err.message);
            }
          }}
          onCancel={() => setConfigModal(null)}
          t={t}
        />
      )}
    </div>
  );
};

export default GovernanceAgentsPage;
